import currency from "currency.js";
import styled from "styled-components";
import { IPerpAggregatedPosition } from "../../interfaces/positions";
import { COLORS, TEXT_COLORS, BORDER_COLORS } from "../../constants/design/colors";
import { SPACING } from "../../constants/design/spacing";
import { Spinner } from "../shared/Spinner";

const BreakdownGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: ${SPACING.three}px;
  width: 100%;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const VenueCard = styled.div`
  border: 1px solid ${BORDER_COLORS.two};
  border-radius: 8px;
  padding: ${SPACING.three}px;
  background: rgba(255, 255, 255, 0.02);
`;

const VenueLabel = styled.div`
  font-size: 12px;
  color: ${TEXT_COLORS.three};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: ${SPACING.one}px;
`;

const VenueValue = styled.div<{ $positive?: boolean; $negative?: boolean }>`
  font-size: 18px;
  font-weight: 600;
  color: ${({ $positive, $negative }) => {
    if ($positive) return COLORS.green.one;
    if ($negative) return COLORS.red.one;
    return TEXT_COLORS.one;
  }};
`;

const SummaryRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${SPACING.four}px;
  align-items: center;
  margin-bottom: ${SPACING.four}px;
  padding-bottom: ${SPACING.three}px;
  border-bottom: 1px solid ${BORDER_COLORS.two};
`;

const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SummaryLabel = styled.span`
  font-size: 12px;
  color: ${TEXT_COLORS.three};
  text-transform: uppercase;
`;

const SummaryValue = styled.span<{ $color?: string }>`
  font-size: 20px;
  font-weight: 600;
  color: ${({ $color }) => $color ?? TEXT_COLORS.one};
`;

interface IPerpDataDeltaBreakdownProps {
  position?: IPerpAggregatedPosition;
  isLoading: boolean;
}

const formatUsd = (contracts: number, markPrice: number) => {
  const usd = contracts * markPrice;
  if (Math.abs(usd) < 0.01) return "—";
  return currency(usd).format();
};

const valueTone = (usd: number) => {
  if (usd > 0.01) return { positive: true, negative: false };
  if (usd < -0.01) return { positive: false, negative: true };
  return { positive: false, negative: false };
};

const toneColor = (tone: ReturnType<typeof valueTone>) => {
  if (tone.positive) return COLORS.green.one;
  if (tone.negative) return COLORS.red.one;
  return TEXT_COLORS.one;
};

export function PerpDataDeltaBreakdown({ position, isLoading }: IPerpDataDeltaBreakdownProps) {
  if (isLoading && !position) {
    return <Spinner />;
  }

  if (!position) {
    return <div style={{ color: TEXT_COLORS.three }}>No position data for this asset.</div>;
  }

  const mark = position.markPrice;
  const netUsd = position.netDelta * mark;
  const netTone = valueTone(netUsd);
  let side = "Flat";
  let sideColor: string = TEXT_COLORS.three;
  if (position.contracts > 0) {
    side = "Long";
    sideColor = COLORS.green.one;
  } else if (position.contracts < 0) {
    side = "Short";
    sideColor = COLORS.red.one;
  }

  const venues = [
    { label: "Aevo", contracts: position.aevoDelta, mark: position.aevoMarkPrice || mark },
    { label: "Binance", contracts: position.binanceDelta, mark: position.binanceMarkPrice || mark },
    { label: "Bybit", contracts: position.bybitDelta, mark: position.bybitMarkPrice || mark },
    {
      label: "Hyperliquid",
      contracts: position.hyperliquidDelta,
      mark: position.hyperliquidMarkPrice || mark,
    },
    { label: "Lighter Mainnet", contracts: position.lighterMainnetDelta, mark: position.lighterMainnetMarkPrice || mark },
    {
      label: "Lighter Robinhood",
      contracts: position.lighterDelta,
      mark: position.lighterMarkPrice || mark,
    },
  ];

  return (
    <div>
      <SummaryRow>
        <SummaryItem>
          <SummaryLabel>Instrument</SummaryLabel>
          <SummaryValue>{position.instrument_name}</SummaryValue>
        </SummaryItem>
        <SummaryItem>
          <SummaryLabel>Net side</SummaryLabel>
          <SummaryValue $color={sideColor}>{side}</SummaryValue>
        </SummaryItem>
        <SummaryItem>
          <SummaryLabel>Net delta (USD)</SummaryLabel>
          <SummaryValue $color={toneColor(netTone)}>
            {formatUsd(position.netDelta, mark)}
          </SummaryValue>
        </SummaryItem>
        <SummaryItem>
          <SummaryLabel>Mark</SummaryLabel>
          <SummaryValue>
            {mark ? currency(mark).format() : "—"}
          </SummaryValue>
        </SummaryItem>
      </SummaryRow>

      <BreakdownGrid>
        {venues.map((venue) => {
          const usd = venue.contracts * venue.mark;
          const tone = valueTone(usd);
          return (
            <VenueCard key={venue.label}>
              <VenueLabel>{venue.label}</VenueLabel>
              <VenueValue $positive={tone.positive} $negative={tone.negative}>
                {formatUsd(venue.contracts, venue.mark)}
              </VenueValue>
            </VenueCard>
          );
        })}
        <VenueCard>
          <VenueLabel>Net</VenueLabel>
          <VenueValue $positive={netTone.positive} $negative={netTone.negative}>
            {formatUsd(position.netDelta, mark)}
          </VenueValue>
        </VenueCard>
      </BreakdownGrid>
    </div>
  );
}
